import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ProgressItem } from '@/types';
import { CURRICULUM } from '@/lib/curriculum';
import ProgressCategoryCard from './ProgressCategoryCard';

interface ProgressSectionProps {
  studentId: string;
}

const STAGES = ['중학교', '고등학교'];

export default function ProgressSection({ studentId }: ProgressSectionProps) {
  const [items, setItems] = useState<ProgressItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [stage, setStage] = useState<string>('고등학교');
  const [pickCategory, setPickCategory] = useState<string>('');
  const [pickUnits, setPickUnits] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('progress_items')
        .select('*')
        .eq('student_id', studentId)
        .order('created_at', { ascending: true });
      if (fetchError) throw fetchError;
      setItems((data as ProgressItem[]) ?? []);
    } catch {
      setError('진도 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const map = new Map<string, ProgressItem[]>();
    items.forEach((it) => {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category)!.push(it);
    });
    return map;
  }, [items]);

  const orderedCategories = useMemo(
    () => CURRICULUM.filter((c) => grouped.has(c.category)).map((c) => c.category),
    [grouped],
  );

  const stats = useMemo(() => {
    const done = items.filter((i) => i.status === 'done').length;
    const total = items.length;
    const remaining = total - done;
    const percent = total === 0 ? 0 : Math.round((done / total) * 100);
    return { done, remaining, total, percent };
  }, [items]);

  const stageCategories = useMemo(() => CURRICULUM.filter((c) => c.stage === stage), [stage]);

  useEffect(() => {
    setPickCategory(stageCategories[0]?.category ?? '');
    setPickUnits([]);
  }, [stageCategories]);

  const pickUnitList = useMemo(
    () => CURRICULUM.find((c) => c.category === pickCategory)?.units ?? [],
    [pickCategory],
  );

  const togglePickUnit = (unit: string) => {
    setPickUnits((prev) =>
      prev.includes(unit) ? prev.filter((u) => u !== unit) : [...prev, unit],
    );
  };

  const selectAllUnits = () => {
    setPickUnits((prev) => (prev.length === pickUnitList.length ? [] : [...pickUnitList]));
  };

  const openPicker = () => {
    setPickerOpen((v) => !v);
    setError('');
  };

  const addSelected = async () => {
    if (!pickCategory || pickUnits.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const existing = new Set((grouped.get(pickCategory) ?? []).map((i) => i.unit));
      const toAdd = pickUnits.filter((u) => !existing.has(u));
      if (toAdd.length === 0) {
        setPickerOpen(false);
        setPickUnits([]);
        return;
      }
      const { data, error: insertError } = await supabase
        .from('progress_items')
        .insert(
          toAdd.map((unit) => ({
            student_id: studentId,
            category: pickCategory,
            unit,
            status: 'planned',
          })),
        )
        .select();
      if (insertError) throw insertError;
      setItems((prev) => [...prev, ...((data as ProgressItem[]) ?? [])]);
      setPickUnits([]);
      setPickerOpen(false);
    } catch {
      setError('단원 추가에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };

  const toggleDone = async (item: ProgressItem) => {
    const next = item.status === 'done' ? 'planned' : 'done';
    setBusyId(item.id);
    setError('');
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: next } : i)));
    try {
      const { error: updateError } = await supabase
        .from('progress_items')
        .update({ status: next })
        .eq('id', item.id);
      if (updateError) throw updateError;
    } catch {
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, status: item.status } : i)));
      setError('진도 상태 변경에 실패했어요.');
    } finally {
      setBusyId(null);
    }
  };

  const completeAll = async (category: string) => {
    if (items.filter((i) => i.category === category && i.status !== 'done').length === 0) return;
    setBusy(true);
    setError('');
    setItems((prev) => prev.map((i) => (i.category === category ? { ...i, status: 'done' } : i)));
    try {
      const { error: updateError } = await supabase
        .from('progress_items')
        .update({ status: 'done' })
        .eq('student_id', studentId)
        .eq('category', category);
      if (updateError) throw updateError;
    } catch {
      setError('전체 완료 처리에 실패했어요.');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const removeItem = async (id: string) => {
    setBusyId(id);
    setError('');
    const prev = items;
    setItems((p) => p.filter((i) => i.id !== id));
    try {
      const { error: deleteError } = await supabase.from('progress_items').delete().eq('id', id);
      if (deleteError) throw deleteError;
    } catch {
      setItems(prev);
      setError('단원 삭제에 실패했어요.');
    } finally {
      setBusyId(null);
    }
  };

  const removeCategory = async (category: string) => {
    setBusy(true);
    setError('');
    const prev = items;
    setItems((p) => p.filter((i) => i.category !== category));
    try {
      const { error: deleteError } = await supabase
        .from('progress_items')
        .delete()
        .eq('student_id', studentId)
        .eq('category', category);
      if (deleteError) throw deleteError;
    } catch {
      setItems(prev);
      setError('교육과정 삭제에 실패했어요.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin"></i>
        불러오는 중...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-xl border border-background-200 bg-gradient-to-br from-primary-50 to-background-100 p-5 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-route-line"></i>
              </span>
              남은 진도 상황
            </h2>
            <p className="mt-1 text-xs text-foreground-500">
              교육과정을 골라 단원을 등록하고, 끝난 단원을 체크하면 남은 진도가 자동 계산돼요.
            </p>
          </div>
          <button
            type="button"
            onClick={openPicker}
            className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
          >
            <i className={pickerOpen ? 'ri-close-line' : 'ri-add-line'}></i>
            {pickerOpen ? '닫기' : '교육과정 추가'}
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="text-xs text-foreground-500">전체 진행률</p>
            <p className="mt-1 text-2xl font-bold text-primary-700">{stats.percent}%</p>
          </div>
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="text-xs text-foreground-500">완료 단원</p>
            <p className="mt-1 text-2xl font-bold text-foreground-950">{stats.done}개</p>
          </div>
          <div className="rounded-xl border border-accent-200 bg-accent-50 p-4">
            <p className="text-xs text-accent-800">남은 진도</p>
            <p className="mt-1 text-2xl font-bold text-accent-900">{stats.remaining}개</p>
          </div>
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="text-xs text-foreground-500">등록 단원</p>
            <p className="mt-1 text-2xl font-bold text-foreground-950">{stats.total}개</p>
          </div>
        </div>

        {stats.total > 0 && (
          <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-background-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all"
              style={{ width: `${stats.percent}%` }}
            />
          </div>
        )}
      </section>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {pickerOpen && (
        <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
          <h3 className="text-sm font-bold text-foreground-950">교육과정 · 단원 선택</h3>
          <p className="mt-1 text-xs text-foreground-500">
            학교급과 교육과정을 고르고, 수업할 단원을 선택하세요.
          </p>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <div className="flex-1">
              <label className="mb-1.5 block text-xs font-medium text-foreground-600">학교급</label>
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="mb-1.5 block text-xs font-medium text-foreground-600">교육과정</label>
              <select
                value={pickCategory}
                onChange={(e) => {
                  setPickCategory(e.target.value);
                  setPickUnits([]);
                }}
                className="w-full rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              >
                {stageCategories.map((c) => (
                  <option key={c.category} value={c.category}>
                    {c.category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs font-semibold text-foreground-700">
              단원 선택 ({pickUnits.length}/{pickUnitList.length})
            </p>
            <button
              type="button"
              onClick={selectAllUnits}
              className="flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              <i className={pickUnits.length === pickUnitList.length ? 'ri-checkbox-multiple-blank-line' : 'ri-checkbox-multiple-line'}></i>
              {pickUnits.length === pickUnitList.length ? '전체 해제' : '전체 선택'}
            </button>
          </div>

          <div className="mt-2.5 flex flex-wrap gap-2">
            {pickUnitList.map((unit) => {
              const active = pickUnits.includes(unit);
              return (
                <button
                  key={unit}
                  type="button"
                  onClick={() => togglePickUnit(unit)}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition whitespace-nowrap ${
                    active
                      ? 'border-primary-400 bg-primary-100 text-primary-700'
                      : 'border-background-300 bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={active ? 'ri-checkbox-circle-fill' : 'ri-add-circle-line'}></i>
                  {unit}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={addSelected}
              disabled={busy || pickUnits.length === 0}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-40 whitespace-nowrap"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
              선택한 단원 추가
            </button>
          </div>
        </section>
      )}

      {orderedCategories.length === 0 ? (
        <p className="rounded-lg border border-dashed border-background-300 py-14 text-center text-sm text-foreground-500">
          아직 등록된 진도가 없어요. &lsquo;교육과정 추가&rsquo;로 단원을 등록해보세요.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {orderedCategories.map((category) => (
            <ProgressCategoryCard
              key={category}
              category={category}
              items={grouped.get(category) ?? []}
              busy={busy}
              busyId={busyId}
              onToggle={toggleDone}
              onRemoveItem={removeItem}
              onCompleteAll={completeAll}
              onRemoveCategory={removeCategory}
            />
          ))}
        </div>
      )}
    </div>
  );
}